import { getGenericResourceById, getGenericResourceList } from '../../../../../api/genericResource'
import { runWithConcurrency } from '../../asyncConcurrency'
import { normalizeGenericResourceDetail, normalizeGenericResourceList, toLinkListQueryParams } from '../../genericResourceAdapter'
import { mergeActionRowWithRefMeta, normalizeMusicLoopForRow } from './workoutActionBindingCalculator'
import { findBindingZone } from './workoutCollectionBindingModel'
import { buildBindingRefMeta, formatBindingRefMetaLines } from './workoutBindingUtils'

/**
 * 编辑页：按 resource_id / resource_db_id 拉子资源 ref_meta（卡片缩略图与摘要）。
 */
export async function fetchBindingRefMetaForRow(assetType, row) {
  if (!row) {
    return null
  }
  let dbId = row.resource_db_id
  if (!dbId && row.resource_id) {
    const listRes = await getGenericResourceList(
      toLinkListQueryParams({
        pageNum: 1,
        pageSize: 1,
        key: row.resource_id,
        assetType,
      })
    )
    const hit = normalizeGenericResourceList(listRes?.data?.wallpaperVOList)[0]
    dbId = hit && hit.id
  }
  if (!dbId) {
    return null
  }
  const detailRes = await getGenericResourceById({ id: dbId })
  if (!detailRes?.success || !detailRes.data) {
    return null
  }
  return buildBindingRefMeta(assetType, detailRes.data)
}

/**
 * Picker 确认后：按 DB id 拉 ref_meta。
 */
export async function fetchBindingRefMetaByDbId(assetType, dbId) {
  if (!dbId) {
    return null
  }
  const detailRes = await getGenericResourceById({ id: dbId })
  if (!detailRes?.success || !detailRes.data) {
    return null
  }
  return buildBindingRefMeta(assetType, detailRes.data)
}

/**
 * 编辑/保存前：按行拉最新动作 ref_meta 并重算编排字段，避免 binding 行缓存时长与资源 exta 漂移。
 */
export async function refreshActionBindingRows(rows) {
  if (!Array.isArray(rows) || !rows.length) {
    return []
  }
  return runWithConcurrency(rows, async (row) => {
    try {
      const meta = await fetchBindingRefMetaForRow('workout_action', row)
      return meta ? mergeActionRowWithRefMeta(row, meta) : row
    } catch (e) {
      return row
    }
  })
}

/**
 * 编辑/配置态：按 zone 拉最新 ref_meta；动作区会重算编排字段，音乐区会归一化 loop。
 */
export async function refreshBindingRowsForZone(zoneId, rows) {
  if (!Array.isArray(rows) || !rows.length) {
    return []
  }
  const zone = findBindingZone(zoneId)
  if (!zone) {
    return rows
  }
  if (zoneId === 'actions') {
    return refreshActionBindingRows(rows)
  }
  return runWithConcurrency(rows, async (row) => {
    try {
      const meta = await fetchBindingRefMetaForRow(zone.assetType, row)
      if (!meta) {
        return row
      }
      return zoneId === 'music'
        ? normalizeMusicLoopForRow({ ...row, ref_meta: meta })
        : { ...row, ref_meta: meta }
    } catch (e) {
      return row
    }
  })
}

/**
 * 按 resource_id（key）拉子资源详情：列表只拿 id/名称，详情接口才带完整 exta。
 * 编辑页 picker 与查看页 enrichment 共用，避免两套请求逻辑漂移。
 */
export async function fetchBindingChildSummary(assetType, resourceId, zoneId) {
  if (!resourceId || !assetType) {
    return { title: resourceId || '—', refLines: [] }
  }
  const listRes = await getGenericResourceList(
    toLinkListQueryParams({
      pageNum: 1,
      pageSize: 1,
      key: resourceId,
      assetType,
    })
  )
  const hit = normalizeGenericResourceList(listRes?.data?.wallpaperVOList)[0]
  const title = hit ? hit.resourceName || hit.key || resourceId : resourceId
  if (!hit?.id) {
    return { title, refLines: [] }
  }
  const detailRes = await getGenericResourceById({ id: hit.id })
  if (!detailRes?.success || !detailRes.data) {
    return { title, refLines: [] }
  }
  const detail = normalizeGenericResourceDetail(detailRes.data)
  const refLines = formatBindingRefMetaLines(buildBindingRefMeta(assetType, detail), zoneId)
  return { title, refLines }
}
